import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BellRing, BookOpen, HardHat, Loader2, Pencil, Plus, RefreshCcw, Settings2, Trash2, Upload, Users, X } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { CopilotDock } from "../../components/os/CopilotDock";
import { CrewRanking } from "../../components/os/CrewRanking";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet transition-colors";

const ESTADO_META = {
  disponible: ["Disponible", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  ocupada: ["Ocupada", "bg-amber-50 text-amber-700 border-amber-200"],
  inactiva: ["Inactiva", "bg-navy/5 text-navy/50 border-navy/15"],
};

const EMPTY = { nombre: "", codigo: "", tipo: "", lider: "", zona: "", efectividad: 100, efectividad_auto: true, estado: "disponible", vehiculo_id: "", notas: "", miembros: [], capacidades: [] };

export default function Cuadrillas() {
  const [items, setItems] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [perf, setPerf] = useState(null);
  const [bitacora, setBitacora] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [recal, setRecal] = useState(false);
  const [modal, setModal] = useState(null); // {..cuadrilla, id?}
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/cuadrillas").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/cuadrillas/performance").then((r) => setPerf(r.data)).catch(() => {});
    api.get("/cuadrillas/bitacora").then((r) => setBitacora(r.data)).catch(() => {});
    api.get("/cuadrillas/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/fleet/units").then((r) => setFleet(r.data || [])).catch(() => {});
  }, [load]);

  const del = async (c) => {
    if (!window.confirm(`¿Eliminar la cuadrilla "${c.nombre}"? (irá a la Papelera)`)) return;
    try { await api.delete(`/cuadrillas/${c.id}`); toast.success("Cuadrilla eliminada"); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  const importExcel = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/cuadrillas/import-excel", fd);
      setImportPreview(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "No pude interpretar el archivo"); }
    finally { setImporting(false); }
  };

  const confirmImport = async () => {
    try {
      const { data } = await api.post("/cuadrillas/bulk", { items: importPreview.cuadrillas });
      toast.success(`${data.added} cuadrilla(s) importadas`);
      setImportPreview(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error al importar"); }
  };

  const checkAlerts = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/cuadrillas/check-alerts");
      if (data.nuevas) toast.warning(`${data.nuevas} alerta(s) de atrasos generadas`);
      else toast.success("Sin cuadrillas por encima del umbral de atrasos");
      load();
    } catch { toast.error("Error al verificar atrasos"); }
    finally { setChecking(false); }
  };

  const recalibrate = async () => {
    setRecal(true);
    try {
      const { data } = await api.post("/cuadrillas/recalibrate");
      toast.success(data.ajustadas ? `${data.ajustadas} efectividad(es) recalibradas con el histórico real`
        : "Sin cambios: se requieren ≥10 actividades exigidas por cuadrilla en planes iniciados");
      load();
    } catch { toast.error("Error al recalibrar"); }
    finally { setRecal(false); }
  };

  const saveSettings = async () => {
    try {
      const { data } = await api.patch("/cuadrillas/settings", settings);
      setSettings(data);
      setSettingsOpen(false);
      toast.success("Configuración guardada");
    } catch { toast.error("Error al guardar la configuración"); }
  };

  const kpis = items ? {
    total: items.length,
    disponible: items.filter((c) => (c.estado_efectivo || c.estado) === "disponible").length,
    ocupada: items.filter((c) => (c.estado_efectivo || c.estado) === "ocupada").length,
    inactiva: items.filter((c) => (c.estado_efectivo || c.estado) === "inactiva").length,
  } : null;

  return (
    <OSLayout title="OMNIA Cuadrillas">
      <div className="space-y-6 p425" data-testid="cuadrillas-page">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-navy/50 max-w-2xl">
            Registra tus cuadrillas: composición, líder, efectividad, capacidades por tipo de trabajo o poste,
            zona que cubren y estado. OMNIA Planning las usa como recursos reales al armar cada plan.
          </p>
          <div className="ml-auto flex gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="cuadrillas-import-input" onChange={importExcel} />
            <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="cuadrillas-import-btn"
              className="rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-violet hover:text-violet inline-flex items-center gap-2 disabled:opacity-50">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Importar Excel
            </button>
            <button onClick={() => setModal({ ...EMPTY })} data-testid="cuadrillas-new-btn"
              className="rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Nueva cuadrilla
            </button>
          </div>
        </div>

        {kpis && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cuadrillas-kpis">
            {[["Cuadrillas", kpis.total], ["Disponibles", kpis.disponible], ["Ocupadas", kpis.ocupada], ["Inactivas", kpis.inactiva]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-navy/10 bg-white px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-navy/40">{l}</div>
                <div className="font-medium text-sm mt-0.5">{v}</div>
              </div>
            ))}
          </div>
        )}

        <CrewRanking ranking={perf?.ranking} today={perf?.today} showPlanes testid="cuadrillas-ranking" />

        <div className="rounded-xl border border-navy/10 bg-white" data-testid="cuadrillas-bitacora">
          <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-navy/5">
            <BookOpen className="w-4 h-4 text-violet" />
            <span className="font-medium text-sm">Bitácora — alertas y ajustes automáticos</span>
            <div className="ml-auto flex items-center gap-2">
              <button onClick={checkAlerts} disabled={checking} data-testid="check-alerts-btn"
                className="rounded-full border border-navy/15 text-xs px-3 py-1.5 hover:border-violet hover:text-violet inline-flex items-center gap-1.5 disabled:opacity-50">
                {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Verificar atrasos
              </button>
              <button onClick={recalibrate} disabled={recal} data-testid="recalibrate-btn"
                className="rounded-full border border-navy/15 text-xs px-3 py-1.5 hover:border-violet hover:text-violet inline-flex items-center gap-1.5 disabled:opacity-50">
                {recal ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />} Recalibrar efectividad
              </button>
              <button onClick={() => setSettingsOpen((v) => !v)} data-testid="cuadrillas-settings-btn"
                className="text-navy/40 hover:text-violet" title="Configuración de alertas">
                <Settings2 className="w-4 h-4" /></button>
            </div>
          </div>
          {settingsOpen && settings && (
            <div className="px-4 py-3 border-b border-navy/5 flex flex-wrap items-end gap-4 bg-navy/[0.02]" data-testid="cuadrillas-settings">
              <label className="text-[10px] text-navy/40">Umbral de atrasos (actividades vencidas)
                <input type="number" min={1} className={`${inputCls} w-32`} value={settings.umbral_atrasos} data-testid="settings-umbral"
                  onChange={(e) => setSettings({ ...settings, umbral_atrasos: Number(e.target.value) })} /></label>
              <label className="flex items-center gap-2 text-sm text-navy/60 pb-2.5">
                <input type="checkbox" checked={!!settings.alertas_email} data-testid="settings-email"
                  onChange={(e) => setSettings({ ...settings, alertas_email: e.target.checked })} />
                Enviar alertas también por correo
              </label>
              <button onClick={saveSettings} data-testid="settings-save-btn"
                className="rounded-full bg-gradient-violet text-white text-xs px-4 py-2 hover:opacity-90 mb-1">Guardar</button>
            </div>
          )}
          <div className="divide-y divide-navy/5 max-h-72 overflow-y-auto">
            {bitacora.length === 0 ? (
              <p className="px-4 py-5 text-sm text-navy/40">
                Sin eventos aún: cada día OMNIA revisa los planes en ejecución, te alerta si una cuadrilla acumula
                {" "}{settings?.umbral_atrasos ?? 5}+ actividades vencidas y recalibra su efectividad con el cumplimiento real.
              </p>
            ) : bitacora.map((e) => (
              <div key={e.id} className="px-4 py-2.5 flex items-start gap-2.5" data-testid={`bitacora-entry-${e.tipo}-${e.cuadrilla_id}`}>
                {e.tipo === "alerta_atrasos"
                  ? <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  : <RefreshCcw className="w-4 h-4 text-violet shrink-0 mt-0.5" />}
                <div className="min-w-0">
                  <div className="text-sm font-medium">{e.titulo}</div>
                  <div className="text-xs text-navy/50">{e.detalle}</div>
                </div>
                <span className="ml-auto text-[10px] text-navy/35 whitespace-nowrap shrink-0">{e.fecha}</span>
              </div>
            ))}
          </div>
        </div>

        {items === null ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-navy/30" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-navy/15 bg-white p-14 text-center" data-testid="cuadrillas-empty">
            <HardHat className="w-10 h-10 mx-auto text-navy/20 mb-3" />
            <p className="font-medium">Aún no tienes cuadrillas</p>
            <p className="text-sm text-navy/40 mt-1">Crea una manualmente o importa tu listado desde Excel.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((c) => {
              const efectivo = c.estado_efectivo || c.estado;
              const autoOcupada = c.ocupada_por && c.estado === "disponible";
              const [el, ecls] = ESTADO_META[efectivo] || ESTADO_META.disponible;
              return (
                <div key={c.id} className="rounded-xl border border-navy/10 bg-white p-5 space-y-3" data-testid={`cuadrilla-card-${c.id}`}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        <HardHat className="w-4 h-4 text-violet shrink-0" /> {c.nombre}
                        {c.codigo && <span className="text-[11px] text-navy/40 font-normal">({c.codigo})</span>}
                      </div>
                      <div className="text-xs text-navy/40 mt-0.5">{c.tipo || "Sin tipo"}{c.zona ? ` · ${c.zona}` : ""}</div>
                    </div>
                    <span className={`ml-auto text-[11px] px-2.5 py-1 rounded-full border shrink-0 ${ecls}`} data-testid={`cuadrilla-estado-${c.id}`}>
                      {autoOcupada ? "Ocupada · auto" : el}
                    </span>
                  </div>
                  {autoOcupada && (
                    <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" data-testid={`cuadrilla-ocupada-por-${c.id}`}>
                      Hoy en "{c.ocupada_por.plan}": {c.ocupada_por.actividad} ({c.ocupada_por.unidad}) · hasta {c.ocupada_por.hasta}
                    </div>
                  )}
                  <div className="text-xs text-navy/50 flex flex-wrap gap-x-4 gap-y-1">
                    {c.lider && <span>Líder: <span className="text-navy/70">{c.lider}</span></span>}
                    <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {(c.miembros || []).length} miembro(s)</span>
                    <span data-testid={`cuadrilla-efectividad-view-${c.id}`}>Efectividad: <span className="text-navy/70">{c.efectividad}%</span>
                      {c.efectividad_auto !== false && <span className="text-[10px] text-violet ml-1">auto</span>}
                      {(c.efectividad_historial || []).length > 0 && (
                        <span className="text-[10px] text-navy/40 ml-1" title="Último ajuste automático por cumplimiento real">
                          ({c.efectividad_historial[c.efectividad_historial.length - 1].nueva > c.efectividad_historial[c.efectividad_historial.length - 1].anterior ? "↗" : "↘"} antes {c.efectividad_historial[c.efectividad_historial.length - 1].anterior}%)
                        </span>
                      )}
                    </span>
                    {c.vehiculo_nombre && <span>Vehículo: <span className="text-navy/70">{c.vehiculo_nombre}</span></span>}
                  </div>
                  {(c.capacidades || []).length > 0 && (
                    <div className="space-y-1">
                      <div className="text-[10px] uppercase tracking-wider text-navy/40">Capacidades</div>
                      {(c.capacidades || []).slice(0, 4).map((cap, i) => (
                        <div key={i} className="text-xs text-navy/60">
                          {cap.trabajo}{cap.condicion ? ` (${cap.condicion})` : ""}: <span className="font-medium">{cap.capacidad} {cap.unidad}</span>
                        </div>
                      ))}
                      {(c.capacidades || []).length > 4 && <div className="text-[11px] text-navy/35">+{c.capacidades.length - 4} más…</div>}
                    </div>
                  )}
                  <div className="flex gap-2 pt-1 border-t border-navy/5">
                    <button onClick={() => setModal({ ...EMPTY, ...c })} data-testid={`cuadrilla-edit-${c.id}`}
                      className="text-xs text-navy/50 hover:text-violet inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                    <button onClick={() => del(c)} data-testid={`cuadrilla-delete-${c.id}`}
                      className="ml-auto text-xs text-navy/30 hover:text-destructive inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {modal && <CuadrillaModal modal={modal} setModal={setModal} fleet={fleet} onSaved={() => { setModal(null); load(); }} />}

      {importPreview && (
        <div className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-cream rounded-lg w-full max-w-3xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="cuadrillas-import-preview">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">OMNIA interpretó {importPreview.total} cuadrilla(s)</h3>
              <button onClick={() => setImportPreview(null)} className="text-navy/40 hover:text-navy" data-testid="import-preview-close"><X className="w-4 h-4" /></button>
            </div>
            {importPreview.resumen && <p className="text-sm text-navy/60">{importPreview.resumen}</p>}
            <div className="space-y-2">
              {importPreview.cuadrillas.map((c, i) => (
                <div key={i} className="rounded-lg border border-navy/10 bg-white p-3 text-sm">
                  <span className="font-medium">{c.nombre}</span>
                  <span className="text-navy/50"> · {c.tipo || "sin tipo"} · {c.zona || "sin zona"} · {c.efectividad}% · {(c.miembros || []).length} miembro(s) · {(c.capacidades || []).length} capacidad(es)</span>
                  {(c.capacidades || []).length > 0 && (
                    <div className="text-xs text-navy/50 mt-1">
                      {(c.capacidades || []).map((cp, j) => <div key={j}>· {cp.trabajo}{cp.condicion ? ` (${cp.condicion})` : ""}: {cp.capacidad} {cp.unidad}</div>)}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={confirmImport} data-testid="import-preview-confirm"
                className="rounded-full bg-gradient-violet text-white text-sm px-6 py-2.5 hover:opacity-90">Confirmar importación</button>
              <button onClick={() => setImportPreview(null)} className="rounded-full border border-navy/15 text-sm px-4 py-2.5 hover:border-navy/40">Cancelar</button>
            </div>
          </div>
        </div>
      )}
      <CopilotDock title="Copiloto · Cuadrillas"
        suggestions={["¿Cuántas cuadrillas tengo disponibles?", "¿Qué cuadrilla tiene mayor capacidad de postes?", "¿Qué cuadrillas cubren Provincia?"]} />
    </OSLayout>
  );
}

function CuadrillaModal({ modal, setModal, fleet, onSaved }) {
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setModal((p) => ({ ...p, [k]: v }));
  const updList = (key, i, field, value) => {
    const list = [...(modal[key] || [])];
    list[i] = { ...list[i], [field]: value };
    set(key, list);
  };
  const rmList = (key, i) => set(key, (modal[key] || []).filter((_, j) => j !== i));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (modal.id) await api.patch(`/cuadrillas/${modal.id}`, modal);
      else await api.post("/cuadrillas", modal);
      toast.success(modal.id ? "Cuadrilla actualizada" : "Cuadrilla creada");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error al guardar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={save} className="bg-cream rounded-lg w-full max-w-2xl p-6 space-y-4 max-h-[92vh] overflow-y-auto" data-testid="cuadrilla-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-medium flex items-center gap-2"><HardHat className="w-4 h-4 text-violet" /> {modal.id ? "Editar cuadrilla" : "Nueva cuadrilla"}</h3>
          <button type="button" onClick={() => setModal(null)} className="text-navy/40 hover:text-navy" data-testid="cuadrilla-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input className={inputCls} placeholder="Nombre *" value={modal.nombre} required data-testid="cuadrilla-nombre"
            onChange={(e) => set("nombre", e.target.value)} />
          <input className={inputCls} placeholder="Código" value={modal.codigo} data-testid="cuadrilla-codigo"
            onChange={(e) => set("codigo", e.target.value)} />
          <input className={inputCls} placeholder="Tipo (postes, tendido, tierras…)" value={modal.tipo} data-testid="cuadrilla-tipo"
            onChange={(e) => set("tipo", e.target.value)} />
          <input className={inputCls} placeholder="Líder" value={modal.lider} data-testid="cuadrilla-lider"
            onChange={(e) => set("lider", e.target.value)} />
          <input className={inputCls} placeholder="Zona / área que cubre" value={modal.zona} data-testid="cuadrilla-zona"
            onChange={(e) => set("zona", e.target.value)} />
          <label className="text-[10px] text-navy/40">Efectividad (%)
            <input className={inputCls} type="number" min={1} max={150} value={modal.efectividad} data-testid="cuadrilla-efectividad"
              onChange={(e) => set("efectividad", Number(e.target.value))} /></label>
          <select className={inputCls} value={modal.estado} data-testid="cuadrilla-estado-select" onChange={(e) => set("estado", e.target.value)}>
            <option value="disponible">Disponible</option>
            <option value="ocupada">Ocupada</option>
            <option value="inactiva">Inactiva</option>
          </select>
          <select className={inputCls} value={modal.vehiculo_id} data-testid="cuadrilla-vehiculo" onChange={(e) => set("vehiculo_id", e.target.value)}>
            <option value="">Vehículo de Flotilla…</option>
            {fleet.map((u) => <option key={u.id} value={u.id}>{u.nombre || u.placas || u.id.slice(0, 6)}</option>)}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-navy/60">
          <input type="checkbox" checked={modal.efectividad_auto !== false} data-testid="cuadrilla-efectividad-auto"
            onChange={(e) => set("efectividad_auto", e.target.checked)} />
          Ajuste automático de efectividad según el cumplimiento real de planes pasados
        </label>

        <div className="rounded-lg border border-navy/10 bg-white p-3 space-y-2">
          <div className="flex items-center">
            <div className="text-[10px] uppercase tracking-wider text-navy/40">Miembros</div>
            <button type="button" data-testid="cuadrilla-add-miembro" onClick={() => set("miembros", [...(modal.miembros || []), { nombre: "", rol: "" }])}
              className="ml-auto rounded-full border border-navy/15 text-xs px-3 py-1 hover:border-violet inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar</button>
          </div>
          {(modal.miembros || []).map((m, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_28px] gap-2">
              <input className={inputCls} placeholder="Nombre" value={m.nombre} onChange={(e) => updList("miembros", i, "nombre", e.target.value)} />
              <input className={inputCls} placeholder="Rol (liniero, chofer…)" value={m.rol} onChange={(e) => updList("miembros", i, "rol", e.target.value)} />
              <button type="button" onClick={() => rmList("miembros", i)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-navy/10 bg-white p-3 space-y-2">
          <div className="flex items-center gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-navy/40">Capacidades por tipo de trabajo</div>
              <p className="text-[11px] text-navy/35">Ej.: "cambio de postes" (Provincia) 8 postes/día · "postes de internet" 8 postes/día · "postes de telefonía" 2 postes/día</p>
            </div>
            <button type="button" data-testid="cuadrilla-add-capacidad" onClick={() => set("capacidades", [...(modal.capacidades || []), { trabajo: "", condicion: "", capacidad: 1, unidad: "" }])}
              className="ml-auto rounded-full border border-navy/15 text-xs px-3 py-1 hover:border-violet inline-flex items-center gap-1 shrink-0"><Plus className="w-3 h-3" /> Agregar</button>
          </div>
          {(modal.capacidades || []).map((c, i) => (
            <div key={i} className="grid grid-cols-[1.4fr_1fr_80px_1fr_28px] gap-2">
              <input className={inputCls} placeholder="Trabajo / actividad" value={c.trabajo} data-testid={`capacidad-trabajo-${i}`}
                onChange={(e) => updList("capacidades", i, "trabajo", e.target.value)} />
              <input className={inputCls} placeholder="Condición (zona, tipo…)" value={c.condicion}
                onChange={(e) => updList("capacidades", i, "condicion", e.target.value)} />
              <input className={inputCls} type="number" step="0.1" min={0.1} value={c.capacidad} data-testid={`capacidad-valor-${i}`}
                onChange={(e) => updList("capacidades", i, "capacidad", Number(e.target.value))} />
              <input className={inputCls} placeholder="Unidad (postes/día)" value={c.unidad}
                onChange={(e) => updList("capacidades", i, "unidad", e.target.value)} />
              <button type="button" onClick={() => rmList("capacidades", i)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        <textarea className={inputCls} rows={2} placeholder="Notas" value={modal.notas} onChange={(e) => set("notas", e.target.value)} />
        <button disabled={busy} data-testid="cuadrilla-submit"
          className="w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {modal.id ? "Guardar cambios" : "Crear cuadrilla"}
        </button>
      </form>
    </div>
  );
}
